// Keep this module dependency-free: it runs before React, auth, or PDF.js loads.
// Requirements and sources: cat/docs/browser-support.md.
export const BROWSER_UPDATE_GUIDES = {
    chrome: { name: 'Chrome', url: 'https://support.google.com/chrome/answer/95414' },
    edge: { name: 'Edge', url: 'https://support.microsoft.com/en-US/edge/microsoft-edge-update-settings' },
    firefox: { name: 'Firefox', url: 'https://support.mozilla.org/en-US/kb/update-firefox-latest-release' },
    safari: { name: 'Safari', url: 'https://support.apple.com/en-us/102665' },
    opera: { name: 'Opera', url: 'https://help.opera.com/en/latest/crashes-and-issues/#updateBrowser' },
    samsung: {
        name: 'Samsung Internet',
        url: 'https://www.samsung.com/levant/support/apps-services/install-or-update-samsung-internet/',
    },
    brave: { name: 'Brave', url: 'https://support.brave.app/hc/en-us/articles/37970926931853-How-do-I-update-Brave' },
    vivaldi: { name: 'Vivaldi', url: 'https://help.vivaldi.com/desktop/install-update/update-vivaldi/' },
};

export const MINIMUM_BROWSER_VERSIONS = {
    chrome: '119',
    edge: '119',
    firefox: '124',
    safari: '17.4',
    opera: '105',
    samsung: '25',
};

const atLeast = (version, minimum) => {
    if (!version) return false;
    const actual = version.split(/[._]/).map(Number);
    const required = minimum.split('.').map(Number);
    for (let i = 0; i < required.length; i += 1) {
        const part = actual[i] || 0;
        if (part !== required[i]) return part > required[i];
    }
    return true;
};

const readVersion = (ua, pattern) => {
    const match = ua.match(pattern);
    return match ? match[1] : null;
};

export const getBrowserCompatibility = (navigatorInfo = window.navigator, runtime = window) => {
    const ua = navigatorInfo.userAgent || '';
    // Specific brands must precede Chrome/Safari, whose tokens they also include.
    const brands = [
        ['edge', /(?:EdgA?|EdgiOS|Edge)\/([\d.]+)/],
        ['opera', /(?:OPR|OPiOS|Opera)\/([\d.]+)/],
        ['samsung', /SamsungBrowser\/([\d.]+)/],
        ['vivaldi', /Vivaldi\/([\d.]+)/],
        ['firefox', /(?:Firefox|FxiOS)\/([\d.]+)/],
        ['chrome', /(?:Chrome|Chromium|CriOS)\/([\d.]+)/],
        ['safari', /Version\/([\d.]+).*Safari\//],
    ];
    let browser = 'unknown';
    let version = null;
    for (const [name, pattern] of brands) {
        const detectedVersion = readVersion(ua, pattern);
        if (detectedVersion) {
            browser = name;
            version = detectedVersion;
            break;
        }
    }
    if (browser === 'chrome' && navigatorInfo.brave) browser = 'brave';

    const chromiumVersion = readVersion(ua, /(?:Chrome|Chromium)\/([\d.]+)/);
    const ios = /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigatorInfo.maxTouchPoints > 1);
    let versionSupported = false;
    let updateUrl = BROWSER_UPDATE_GUIDES[browser] ? BROWSER_UPDATE_GUIDES[browser].url : null;

    if (ios) {
        // WebKit on iOS is updated with the OS, even in Chrome/Firefox/Edge.
        // In iOS Chrome/Edge/Firefox desktop mode, Version/ is a Safari
        // compatibility token, not the installed engine version. If the real
        // OS version is hidden, require the runtime capabilities below instead.
        const engineVersion =
            readVersion(ua, /(?:CPU (?:iPhone )?OS|iPhone OS) ([\d_]+)/) ||
            (browser === 'safari' ? readVersion(ua, /Version\/([\d.]+)/) : null);
        versionSupported = !engineVersion || atLeast(engineVersion, MINIMUM_BROWSER_VERSIONS.safari);
        updateUrl = BROWSER_UPDATE_GUIDES.safari.url;
    } else if (browser === 'brave' || browser === 'vivaldi' || (browser === 'opera' && /Android/.test(ua))) {
        // Chromium derivatives and Opera Android have their own version numbering.
        versionSupported = atLeast(chromiumVersion, MINIMUM_BROWSER_VERSIONS.chrome);
    } else if (MINIMUM_BROWSER_VERSIONS[browser]) {
        versionSupported = atLeast(version, MINIMUM_BROWSER_VERSIONS[browser]);
        if (chromiumVersion)
            versionSupported = versionSupported && atLeast(chromiumVersion, MINIMUM_BROWSER_VERSIONS.chrome);
    }

    // Validate capabilities as well as UA versions (embedded browsers and overrides).
    const requiredFeatures = {
        'Promise.withResolvers': runtime.Promise && runtime.Promise.withResolvers,
        'AbortSignal.any': runtime.AbortSignal && runtime.AbortSignal.any,
        'Array.prototype.findLast': runtime.Array && runtime.Array.prototype && runtime.Array.prototype.findLast,
        structuredClone: runtime.structuredClone,
    };
    const missingFeatures = Object.keys(requiredFeatures).filter(
        (name) => typeof requiredFeatures[name] !== 'function'
    );

    return {
        browser,
        version,
        updateUrl,
        missingFeatures,
        supported: versionSupported && missingFeatures.length === 0,
    };
};
